import { serializeQueryArgs } from '~services/utils';
import { api, apiTags, transformGridFilters } from '~shared/api/origin';
import { isSafeApp } from '~shared/utils/host';
import type { RootState } from '~store/index';
import indexDb from '~utils/indexDb';

import type {
	GetGridApiArgs,
	GetGridApiResponse,
	TGridFiltersResponse,
} from './types';

export const gridApi = api.injectEndpoints({
	endpoints: (build) => ({
		getGridFilters: build.query<TGridFiltersResponse, void>({
			query: () => ({ url: `/ai/chats/search/filters` }),
			transformResponse: (data: TGridFiltersResponse) =>
				transformGridFilters(data),
		}),

		botClicked: build.mutation<void, { userId: string; botId: number }>({
			query: ({ userId, botId }) => ({
				url: `ai/${userId}/bot/${botId}/clicked`,
				method: 'POST',
			}),
		}),

		getGrid: build.query<
			GetGridApiResponse,
			GetGridApiArgs & { cache?: boolean }
		>({
			// @ts-ignore
			queryFn: async (args, queryApi, extraOptions, baseQuery) => {
				const { cache: _, ...arg } = args;
				const isDemoUser = (
					queryApi.getState() as RootState
				).auth.info.roles.includes('demo');
				const key = `${serializeQueryArgs('getGrid', arg)}-${
					isDemoUser ? 'true' : 'false'
				}`;
				const cachedData = (await indexDb.getCache(key)) as GetGridApiResponse;
				if (cachedData) {
					queryApi.dispatch(
						gridApi.util.upsertQueryData(
							'getGrid',
							{ cache: true, ...arg },
							cachedData
						)
					);
				}

				try {
					const response = await baseQuery({
						url: `/ai/grid/${arg.userId}`,
						params: {
							omit: arg.omit,
							select: arg.select,
							q: arg.keyword,
							botTypesMode: arg.createdBy,
							gen: arg.gender,
							desireNames: arg.desireNames,
							ageMin: arg.minAge,
							ageMax: arg.maxAge,
							isSafe: isSafeApp,
						},
					});

					if (response.data) {
						void indexDb.putCache(key, response.data);
					}

					return response;
				} catch (error) {
					return { error };
				}
			},
			forceRefetch({ currentArg, endpointState }) {
				return (
					// @ts-ignore
					// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
					(currentArg?.omit ?? 0) >= (endpointState?.data?.botGrid?.length ?? 0)
				);
			},
			merge: (
				currentState: GetGridApiResponse,
				incomingState: GetGridApiResponse,
				{ arg }
			) => {
				if (!currentState.botGridApi) {
					currentState.botGridApi = currentState.botGrid;
				}
				currentState.hasMoreRows = incomingState.hasMoreRows;

				const botsState = arg.cache
					? currentState.botGrid
					: currentState.botGridApi;
				const botsMap = new Map(botsState.map((item) => [item.uid, item]));

				incomingState.botGrid.forEach((item) => {
					botsMap.set(item.uid, item);
				});

				const botsMerged = Array.from(botsMap.values());
				if (!arg.cache) {
					currentState.botGridApi = botsMerged;
				}
				currentState.botGrid = botsMerged;
			},
			serializeQueryArgs: ({ endpointName, queryArgs }) => {
				const { omit: _, cache: __, ...rest } = queryArgs;
				return serializeQueryArgs(endpointName, rest);
			},
			onCacheEntryAdded(
				{ cache: _, ...arg },
				{ cacheDataLoaded, getCacheEntry, cacheEntryRemoved, getState }
			) {
				const isDemoUser = (getState() as RootState).auth.info.roles.includes(
					'demo'
				);
				const key = `${serializeQueryArgs('getGrid', arg)}-${
					isDemoUser ? 'true' : 'false'
				}`;
				void cacheDataLoaded.then(() => {
					const cacheEntry = getCacheEntry();

					if (cacheEntry?.data) {
						void indexDb.putCache(key, cacheEntry.data);
					}
				});

				void cacheEntryRemoved.then(() => {
					void indexDb.deleteCache(key);
				});
			},
			providesTags: [apiTags.grid],
		}),
	}),
});
